import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { History } from "lucide-react";
import { accountingCommonNames, formatDate } from "shared";
import { IconText } from "@/components/icon-text";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const Route = createFileRoute("/_authed/_org/accounting/sync-history")({
  beforeLoad: async ({ context: { queryClient, trpc } }) => {
    await queryClient.ensureQueryData(trpc.accounting.get.queryOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const { data: orgMembers } = useSuspenseQuery(trpc.org.users.queryOptions());
  const { data: accounting } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );

  const location = useLocation();

  const { data: history } = useSuspenseQuery(
    trpc.accounting.syncHistory.queryOptions(),
  );

  if (location.pathname !== "/accounting/sync-history") {
    return <Outlet />;
  }

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-border border-b p-6 pb-4">
        <SheetTitle>Sync History</SheetTitle>
        {history.length > 0 && (
          <span className="text-neutral-600 text-sm">
            Last Sync: {formatDate(history[0].createdAt)}
          </span>
        )}
      </SheetHeader>

      {history.length === 0 && (
        <div className="flex grow flex-col items-center justify-center">
          <div className="rounded-md bg-neutral-100 p-4">
            <History className="size-8 text-neutral-600" />
          </div>
          <h3 className="mt-6 text-neutral-900 text-xl">
            No Past Sync History
          </h3>

          <p className="mt-2 mb-4 max-w-lg text-center text-neutral-700 text-sm">
            You can sync your Givefront transactions to{" "}
            {accountingCommonNames[accounting!.type]} from the Accounting page.
            Once you do so, you'll see your sync history here.
          </p>
        </div>
      )}

      <div className="flex flex-col overflow-y-auto p-6">
        {history.map((s) => {
          return (
            <Link
              className="flex w-full flex-row items-center justify-between not-last:border-b px-2 py-4 text-neutral-800 hover:bg-neutral-50"
              to="/accounting/sync-history/$id"
              params={{ id: s.id }}
              key={s.id}
            >
              <div className="flex flex-col">
                <span className="font-medium">{formatDate(s.createdAt)}</span>

                <div className="flex items-center gap-2 text-neutral-600 text-sm">
                  <IconText
                    icon="user"
                    className="flex items-center text-neutral-600"
                  >
                    {orgMembers.userMap[s.createdBy].name ?? "Unknown User"}
                  </IconText>
                </div>
              </div>
              <span>
                {s.transactionCount} transaction
                {s.transactionCount !== 1 && "s"}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
